import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSiteSettings } from "../contexts/SiteSettingsContext";

const EMPTY = { question: "", answer: "", order: 0, isActive: true };

export default function AdminChatbotQA() {
    const { settings, refresh: refreshSettings } = useSiteSettings();
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [greeting, setGreeting] = useState("");
    const [savingGreeting, setSavingGreeting] = useState(false);

    const refresh = () =>
        api.get("/admin/chatbot/qa")
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]));

    useEffect(() => { refresh(); }, []);
    useEffect(() => { setGreeting(settings?.chatbotGreeting || ""); }, [settings?.chatbotGreeting]);

    const startCreate = () => {
        const nextOrder = items.length ? Math.max(...items.map((i) => i.order)) + 1 : 0;
        setEditing({ ...EMPTY, order: nextOrder });
        setOpen(true);
    };
    const startEdit = (qa) => { setEditing({ ...EMPTY, ...qa }); setOpen(true); };
    const close = () => { setOpen(false); setEditing(null); };

    const save = async (e) => {
        e.preventDefault();
        if (!editing) return;
        setBusy(true);
        try {
            const payload = { ...editing, order: Number(editing.order) || 0 };
            if (editing.id) {
                await api.patch(`/admin/chatbot/qa/${editing.id}`, payload);
                toast.success("Q&A updated");
            } else {
                await api.post("/admin/chatbot/qa", payload);
                toast.success("Q&A created");
            }
            close();
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this Q&A?")) return;
        try {
            await api.delete(`/admin/chatbot/qa/${id}`);
            toast.success("Q&A deleted");
            refresh();
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to delete");
        }
    };

    const saveGreeting = async (e) => {
        e.preventDefault();
        setSavingGreeting(true);
        try {
            await api.patch("/admin/site-settings", { chatbotGreeting: greeting ?? "" });
            await refreshSettings();
            toast.success("Greeting updated");
        } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Failed to update");
        } finally {
            setSavingGreeting(false);
        }
    };

    const toggleActive = async (qa) => {
        try {
            await api.patch(`/admin/chatbot/qa/${qa.id}`, { isActive: !qa.isActive });
            refresh();
        } catch (e2) {
            toast.error("Failed to toggle");
        }
    };

    return (
        <div data-testid="admin-chatbot-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div>
                    <span className="label-eyebrow">Conversations</span>
                    <h1 className="mt-2 font-display text-4xl text-inkDark">Chatbot Q&amp;A</h1>
                    <p className="mt-1 text-inkMuted">Curate the questions shown in the storefront chatbot.</p>
                </div>
                <button onClick={startCreate} className="btn-pill text-sm" data-testid="admin-add-qa-button">
                    <Plus size={14} /> Add Q&amp;A
                </button>
            </div>

            <form onSubmit={saveGreeting} className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 p-6 max-w-3xl" data-testid="chatbot-greeting-form">
                <span className="label-eyebrow">Greeting message</span>
                <textarea
                    rows={2}
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5"
                    placeholder="Hi! I'm Sukaanyas Assistant. How can I help?"
                    data-testid="chatbot-greeting-input"
                />
                <div className="mt-3 flex justify-end">
                    <button type="submit" disabled={savingGreeting} className="btn-pill text-sm disabled:opacity-50" data-testid="chatbot-greeting-save">
                        {savingGreeting ? "Saving…" : "Save greeting"}
                    </button>
                </div>
            </form>

            <div className="mt-8 bg-ivory rounded-2xl border border-inkDark/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-qa-table">
                        <thead className="bg-beige text-inkMuted">
                            <tr>
                                <th className="text-left px-4 py-3 font-medium w-16">Order</th>
                                <th className="text-left px-4 py-3 font-medium">Question</th>
                                <th className="text-left px-4 py-3 font-medium">Answer</th>
                                <th className="text-left px-4 py-3 font-medium w-24">Active</th>
                                <th className="text-right px-4 py-3 font-medium w-32">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((qa) => (
                                <tr key={qa.id} className="border-t border-inkDark/5 align-top">
                                    <td className="px-4 py-3 text-inkDark">{qa.order}</td>
                                    <td className="px-4 py-3 text-inkDark font-medium">{qa.question}</td>
                                    <td className="px-4 py-3 text-inkMuted line-clamp-2 max-w-md">{qa.answer}</td>
                                    <td className="px-4 py-3">
                                        <button onClick={() => toggleActive(qa)} className={`text-[10px] px-2 py-1 rounded-full ${qa.isActive ? "bg-green-50 text-green-700" : "bg-inkDark/5 text-inkMuted"}`} data-testid={`qa-active-toggle-${qa.id}`}>
                                            {qa.isActive ? "Active" : "Hidden"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => startEdit(qa)} className="p-2 rounded-lg hover:bg-beige" data-testid={`admin-qa-edit-${qa.id}`}>
                                            <Pencil size={14} />
                                        </button>
                                        <button onClick={() => remove(qa.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" data-testid={`admin-qa-delete-${qa.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 ? (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-inkMuted">No Q&amp;A pairs yet.</td></tr>
                            ) : null}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && editing ? (
                <div className="fixed inset-0 z-50 bg-inkDark/40 flex items-center justify-center p-4" onClick={close}>
                    <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-ivory w-full max-w-xl rounded-2xl shadow-lift" data-testid="admin-qa-modal">
                        <div className="p-6 border-b border-inkDark/5">
                            <span className="label-eyebrow">{editing.id ? "Edit" : "New"}</span>
                            <h2 className="font-display text-2xl">{editing.id ? "Edit Q&A" : "Add Q&A"}</h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 gap-4">
                            <label>
                                <span className="label-eyebrow">Question</span>
                                <input required value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-qa-question-input" />
                            </label>
                            <label>
                                <span className="label-eyebrow">Answer</span>
                                <textarea required rows={4} value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" data-testid="admin-qa-answer-input" />
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <label>
                                    <span className="label-eyebrow">Order</span>
                                    <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} className="mt-2 w-full rounded-xl border border-inkDark/10 bg-white px-4 py-2.5" />
                                </label>
                                <label className="flex flex-col">
                                    <span className="label-eyebrow">Active</span>
                                    <button type="button" onClick={() => setEditing({ ...editing, isActive: !editing.isActive })} className={`mt-2 px-4 py-2 rounded-xl border text-sm ${editing.isActive ? "bg-skyblue/40 border-skyblue text-inkDark" : "bg-white border-inkDark/10 text-inkMuted"}`}>
                                        {editing.isActive ? "Active" : "Hidden"}
                                    </button>
                                </label>
                            </div>
                        </div>
                        <div className="p-6 border-t border-inkDark/5 flex justify-end gap-3">
                            <button type="button" onClick={close} className="btn-ghost text-sm">Cancel</button>
                            <button type="submit" disabled={busy} className="btn-pill text-sm" data-testid="admin-qa-save-button">{busy ? "Saving…" : "Save"}</button>
                        </div>
                    </form>
                </div>
            ) : null}
        </div>
    );
}
